import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, ChevronDown } from 'lucide-react';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { IubendaPolicyLink, CookiePreferencesLink, IUBENDA_PRIVACY_URL, IUBENDA_COOKIE_URL } from '../legal/iubenda';

interface FooterLink {
  to: string;
  label: string;
}

const GROUPS: Array<{ title: string; links: FooterLink[] }> = [
  {
    title: 'Informazioni su DTK',
    links: [
      { to: '/azienda/chi-siamo', label: 'Chi Siamo' },
      { to: '/azienda/mission', label: 'Mission' },
      { to: '/azienda/disponibilita-logistica', label: 'Logistica DTK' },
      { to: '/azienda/area-commerciale', label: 'Area Commerciale' },
      { to: '/azienda/lavora-con-noi', label: 'Lavora con Noi' },
    ],
  },
  {
    title: 'Prodotti',
    links: [
      { to: '/i-nostri-prodotti/toner-cartucce-laser', label: 'Toner e Cartucce Laser' },
      { to: '/i-nostri-prodotti/gruppi-completi', label: 'Gruppi Completi' },
      { to: '/i-nostri-prodotti/ricambi-stampanti-fotocopiatrici', label: 'Ricambi' },
      { to: '/i-nostri-prodotti/attrezzature-tecniche', label: 'Attrezzature Tecniche' },
      { to: '/ktek', label: 'Linee KTEK' },
    ],
  },
  {
    title: 'Supporto',
    links: [
      { to: '/supporto/risorse', label: 'Risorse & Modulistica' },
      { to: '/supporto/resi', label: 'Reso Prodotti' },
      { to: '/supporto/catalogo-online', label: 'Catalogo Online' },
      { to: '/supporto/faq', label: 'FAQ' },
      { to: '/contatti', label: 'Contatti' },
    ],
  },
  {
    title: 'Link Rapidi',
    links: [
      { to: '/accedi', label: 'Accedi' },
      { to: '/registrati', label: 'Diventa Cliente' },
    ],
  },
];

/**
 * Gruppo di link del footer.
 *
 * Su mobile è un `<details>` chiuso: il footer misurava 1.211px, cioè 1,4
 * schermate su *ogni* pagina — il 45% del documento su quelle brevi. Con i
 * gruppi collassati restano visibili logo, contatti diretti e note legali.
 *
 * `<details>` invece di uno stato React: nessun JS, accessibile per
 * default, e degrada correttamente. Da `lg` in su l'attributo `open` è
 * sempre impostato e il marcatore nascosto, così il desktop non cambia.
 */
function FooterGroup({ title, links, forceOpen }: { title: string; links: FooterLink[]; forceOpen: boolean }) {
  return (
    /* `open` è un attributo del DOM, non uno stile: il CSS non può
       aprirlo al breakpoint, da cui il ricorso a matchMedia. Su mobile
       resta `undefined`, così il toggle nativo funziona normalmente. */
    <details
      open={forceOpen || undefined}
      className="group border-b lg:border-b-0 [&_summary::-webkit-details-marker]:hidden lg:[&>summary]:pointer-events-none">
      <summary className="flex min-h-[48px] cursor-pointer list-none items-center justify-between text-sm font-semibold lg:min-h-0 lg:cursor-default lg:pb-3">
        {title}
        <ChevronDown
          className="h-4 w-4 flex-none text-muted-foreground transition-transform group-open:rotate-180 motion-reduce:transition-none lg:hidden"
          aria-hidden="true"
        />
      </summary>
      <ul className="pb-3 lg:pb-0">
        {links.map(l => (
          <li key={l.to}>
            <Link
              to={l.to}
              className="flex min-h-[44px] items-center text-sm text-muted-foreground transition-colors hover:text-foreground lg:min-h-0 lg:py-1"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </details>
  );
}

export function Footer() {
  const isDesktop = useIsDesktop();
  return (
    <footer className="border-t bg-white mt-auto no-print" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img
                src="/logo.png"
                alt="DTK Imaging Supplies"
                className="h-8 w-auto object-contain"
              />
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Distribuzione consumabili e ricambi per stampanti, multifunzione, laser e inkjet.
            </p>
            {/* Telefono ed email erano testo semplice: su mobile sono il
                dato più utile del footer e non erano tappabili. */}
            <div className="mt-4 space-y-1 text-sm text-muted-foreground">
              <div className="flex min-h-[44px] items-center gap-2 lg:min-h-0 lg:py-1">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" /> Via Acuto 52/b, 00131 Roma
              </div>
              <a
                href="tel:+39065135753"
                className="flex min-h-[44px] items-center gap-2 transition-colors hover:text-foreground lg:min-h-0 lg:py-1"
              >
                <Phone className="w-3.5 h-3.5 flex-shrink-0" /> +39 06 5135753
              </a>
              <a
                href="mailto:info@dtksrl.eu"
                className="flex min-h-[44px] items-center gap-2 transition-colors hover:text-foreground lg:min-h-0 lg:py-1"
              >
                <Mail className="w-3.5 h-3.5 flex-shrink-0" /> info@dtksrl.eu
              </a>
              <div className="flex min-h-[44px] items-center gap-2 lg:min-h-0 lg:py-1">
                <Clock className="w-3.5 h-3.5 flex-shrink-0" /> Lun-Ven 9:00-19:00
              </div>
            </div>
          </div>

          {GROUPS.map(g => (
            <nav key={g.title} aria-label={g.title}>
              <FooterGroup title={g.title} links={g.links} forceOpen={isDesktop} />
            </nav>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} DTK S.r.l. - P.IVA 07582561002 - Tutti i diritti riservati.</p>
          {/* `flex-wrap`: su 320px i link legali non stavano in riga.
              Privacy e Cookie sono documenti iubenda (popup). */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <IubendaPolicyLink href={IUBENDA_PRIVACY_URL} className="inline-flex min-h-[44px] items-center hover:text-foreground transition-colors lg:min-h-0">Privacy</IubendaPolicyLink>
            <Link to="/legale/condizioni" className="inline-flex min-h-[44px] items-center hover:text-foreground transition-colors lg:min-h-0">Condizioni</Link>
            <Link to="/legale/note-legali" className="inline-flex min-h-[44px] items-center hover:text-foreground transition-colors lg:min-h-0">Note Legali</Link>
            <IubendaPolicyLink href={IUBENDA_COOKIE_URL} className="inline-flex min-h-[44px] items-center hover:text-foreground transition-colors lg:min-h-0">Cookie</IubendaPolicyLink>
            <CookiePreferencesLink className="inline-flex min-h-[44px] items-center hover:text-foreground transition-colors lg:min-h-0">Preferenze cookie</CookiePreferencesLink>
          </div>
        </div>
        <div className="mt-4 text-right">
          <a href="https://www.toyou.it?utm_source=dtkwebsite&utm_medium=footer&utm_campaign=madeby&utm_id=DTK" target="_blank" rel="noopener noreferrer" className="text-xs text-foreground">Made with ♥️ by TO<strong className="font-bold">YOU</strong></a>
        </div>
      </div>
    </footer>
  );
}
